import React, { useState } from 'react';
import Select from 'components/shared/Select';
import ReactDatePicker from 'react-datepicker';

interface BillingFormData {
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  creditCardNumber: string;
  expirationDate: string;
  cvv: string;
}

const Billing: React.FC = () => {
  const [formData, setFormData] = useState<BillingFormData>({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    creditCardNumber: '',
    expirationDate: '',
    cvv: ''
  });

  const [expiryDateObj, setExpiryDateObj] = useState<Date | null>(null);


  const handleInputChange = (field: keyof BillingFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan'];

  return (
    <div className="mt-10">
      <form onSubmit={handleSubmit}>
        {/* Billing Information Section */}
        <div className="my-5">
          <h2 className="text-lg font-semibold text-white mb-5">Billing information</h2>
          <div className="space-y-6 bg-[#2A2A2A] rounded-lg p-6">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Company Name</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Address Line 1</label>
              <input
                type="text"
                value={formData.addressLine1}
                onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Address Line 2</label>
              <input
                type="text"
                value={formData.addressLine2}
                onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-start gap-8">
              <div className="w-1/3">
                <label className="block text-sm text-gray-300 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="w-1/3">
                <label className="block text-sm text-gray-300 mb-2">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="w-1/3">
                <label className="block text-sm text-gray-300 mb-2">Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Country</label>
              <Select
                value={formData.country}
                onChange={(value) => handleInputChange('country', value)}
                options={countries}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Payment Information Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-6">Payment information</h2>
          <div className="space-y-6 bg-[#2A2A2A] rounded-lg p-6">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Credit card number</label>
              <input
                type="text"
                value={formData.creditCardNumber}
                onChange={(e) => handleInputChange('creditCardNumber', e.target.value)}
                className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-start gap-8">
            <div className="w-1/2">
  <label className="block text-sm text-gray-300 mb-2">Expiration date</label>
  <ReactDatePicker
    selected={expiryDateObj}
    onChange={(date: Date | null) => {
      setExpiryDateObj(date);
      const formatted = date
        ? `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`
        : '';
      handleInputChange('expirationDate', formatted);
    }}
    dateFormat="MM/yy"
    showMonthYearPicker
    placeholderText="MM/YY"
    className="w-full bg-[#232323] rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
</div>


              <div className="w-1/2">
                <label className="block text-sm text-gray-300 mb-2">CVV</label>
                <input
                  type="password"
                  maxLength={4}
                  value={formData.cvv}
                  onChange={(e) => handleInputChange('cvv', e.target.value)}
                  className="w-full bg-[#232323]  rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mt-8">
          <button
          className="flex items-center justify-center text-center gap-2 px-4 py-2 w-[140px] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
            type="button"
            >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-cente justify-center gap-2 px-4 py-2 w-[140px] bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default Billing; 